import { Component, Prop, VModel, Vue } from 'vue-property-decorator';
import DialogLayoutFull from '@/components/DialogLayoutFull/DialogLayoutFull';

@Component<DialogLayoutFullSteps>({})
export default class DialogLayoutFullSteps extends DialogLayoutFull {
  @VModel()
  protected internalValue!: StepInterface;

  @Prop()
  protected steps!: StepInterface[];

  @Prop({ default: true })
  protected canStepBack!: boolean;

  @Prop({ default: true })
  protected canStepForward!: boolean;

  protected onStepClick(step: StepInterface): void {
    if (
      this.internalValue.slot == step.slot
      || (
        ! this.canStepBack
        && this.isStepBeforeCurrentStep(step)
      )
      || (
        ! this.canStepForward
        && this.isStepAfterCurrentStep(step)
      )
    ) {
      return;
    }

    this.internalValue = step;
  }

  protected isStepAfterCurrentStep(step: StepInterface): boolean {
    return isStepAfterCurrentStep(step, this.internalValue, this.steps);
  }

  protected isStepAvailable(step: StepInterface): boolean {
    return isStepAvailable(step);
  }

  protected isStepBeforeCurrentStep(step: StepInterface): boolean {
    return isStepBeforeCurrentStep(step, this.internalValue, this.steps);
  }

  protected isStepVisible(step: StepInterface): boolean {
    return isStepVisible(step);
  }

  protected hasStepVisibleAfterStep(step: StepInterface): boolean {
    return this.stepsVisible.length - 1 !== this.stepsVisible.findIndex((s) => s.slot === step.slot);
  }

  protected get amountStepsAvailable(): number {
    return amountStepsAvailable(this.steps);
  }

  protected get amountStepsVisible(): number {
    return amountStepsVisible(this.steps);
  }

  protected get stepsAvailable(): StepInterface[] {
    return stepsAvailable(this.steps);
  }

  protected get stepsVisible(): StepInterface[] {
    return stepsVisible(this.steps);
  }

  protected get stepCurrentIndex(): number {
    return getStepCurrentIndex(this.internalValue, this.steps);
  }
}

export interface StepInterface {
  label?: string;
  slot: string;
  available?: boolean;
  visible?: boolean;
}

export const amountStepsAvailable = <TStepInterface extends StepInterface>(steps: TStepInterface[]): number => {
  return stepsAvailable(steps).length;
}

export const amountStepsVisible = <TStepInterface extends StepInterface>(steps: TStepInterface[]): number => {
  return stepsVisible(steps).length;
}

export const findNextStep = <TStepInterface extends StepInterface>(stepCurrent: TStepInterface, steps: TStepInterface[]): undefined | TStepInterface => {
  const stepCurrentIndex = getStepCurrentIndex(stepCurrent, steps);

  if (stepCurrentIndex === undefined) { return undefined; }

  let i = 1;
  let nextStep = steps[stepCurrentIndex + i];

  while (nextStep && ! isStepAvailable(nextStep)) {
    i += 1;
    nextStep = steps[stepCurrentIndex + i];
  }

  return nextStep;
}

export const findPreviousStep = <TStepInterface extends StepInterface>(stepCurrent: TStepInterface, steps: TStepInterface[]): undefined | TStepInterface => {
  const stepCurrentIndex = getStepCurrentIndex(stepCurrent, steps);

  if (stepCurrentIndex === undefined) { return undefined; }

  let i = 1;
  let previousStep = steps[stepCurrentIndex - i];

  while (previousStep && ! isStepAvailable(previousStep)) {
    i += 1;
    previousStep = steps[stepCurrentIndex - i];
  }

  return previousStep;
}

export const getStepCurrentIndex = <TStepInterface extends StepInterface>(currentStep: TStepInterface, steps: TStepInterface[]): number => {
  return stepsAvailable(steps).findIndex((step) => step.slot === currentStep.slot);
}

export const isStepAfterCurrentStep = <TStepInterface extends StepInterface>(step: TStepInterface, stepCurrent: TStepInterface, steps: TStepInterface[]): boolean => {
  const stepIndex = steps.findIndex((s) => s.slot === step.slot);

  const stepCurrentIndex = steps.findIndex((step) => step.slot === stepCurrent.slot);

  return stepIndex !== undefined && stepCurrentIndex !== undefined && stepIndex > stepCurrentIndex;
}

export const isStepAvailable = (step: StepInterface): boolean => {
    return step.available === undefined || !! step.available;
  }

export const isStepBeforeCurrentStep = <TStepInterface extends StepInterface>(step: TStepInterface, stepCurrent: TStepInterface, steps: TStepInterface[]): boolean => {
  const stepIndex = steps.findIndex((s) => s.slot === step.slot);

  const stepCurrentIndex = steps.findIndex((step) => step.slot === stepCurrent.slot);

  return stepIndex !== undefined && stepCurrentIndex !== undefined && stepIndex < stepCurrentIndex;
}

export const isStepVisible = <TStepInterface extends StepInterface>(step: TStepInterface): boolean => {
  return step.visible === undefined || !! step.visible;
}

export const stepsAvailable = <TStepInterface extends StepInterface>(steps: TStepInterface[]): TStepInterface[] => {
  return steps.filter((step) => isStepAvailable(step));
}

export const stepsVisible = <TStepInterface extends StepInterface>(steps: TStepInterface[]): TStepInterface[] => {
  return steps.filter((step) => isStepVisible(step));
}

